import { useEffect, useId, useMemo, useRef, useState } from 'react';

import type { EpsgSearchResult } from '../crs/epsgCatalogue';
import type { PendingTabularImport } from '../data/import/dataImport';
import {
  createSpreadsheetPointLayer,
  hasLikelyWgs84AxisReversal,
  previewSpreadsheetPoints,
  suggestCoordinateFields,
  type SpreadsheetSourceCrs,
} from '../data/import/spreadsheetPoints';
import { representativeFieldValue, type ImportedTableData } from '../data/import/tabularImport';
import type { ImportedVectorData } from '../data/import/model';
import { DialogShell } from './DialogShell';

interface SpreadsheetImportDialogProps {
  readonly pending: PendingTabularImport;
  readonly onCancel: () => void;
  readonly onImportTable: (table: ImportedTableData) => void;
  readonly onImportPoints: (
    imported: ImportedVectorData,
    summary: { readonly invalid: number; readonly valid: number },
  ) => void;
  readonly returnFocusTo?: HTMLElement | null | undefined;
  readonly vectorOnly?: boolean;
}

type ImportIntent = 'points' | 'table';
type CoordinateSystemChoice = 'projected' | 'wgs84';

function coordinateDefaults(table: ImportedTableData, vectorOnly = false) {
  const suggestions = suggestCoordinateFields(table);
  const xField = suggestions.xField || table.fields[0]?.name || '';
  const yField =
    suggestions.yField || table.fields.find((field) => field.name !== xField)?.name || '';
  return {
    intent: (vectorOnly || suggestions.strong ? 'points' : 'table') as ImportIntent,
    xField,
    yField,
    crsChoice: (suggestions.xField && !suggestions.likelyGeographic
      ? 'projected'
      : 'wgs84') as CoordinateSystemChoice,
    selectedCrs: suggestions.likelyGeographic
      ? ({ kind: 'wgs84' } as const satisfies SpreadsheetSourceCrs)
      : null,
  };
}

function fieldTypeLabel(type: ImportedTableData['fields'][number]['type']): string {
  if (type === 'integer' || type === 'number') return 'Number';
  if (type === 'boolean') return 'Boolean';
  if (type === 'date') return 'Date';
  return 'Text';
}

function fieldOptionLabel(table: ImportedTableData, name: string): string {
  const field = table.fields.find((candidate) => candidate.name === name);
  if (!field) return name;
  const example = representativeFieldValue(table, name);
  return `${name} — ${fieldTypeLabel(field.type)}${example === null ? '' : ` · Example: ${String(example)}`}`;
}

export function SpreadsheetImportDialog({
  pending,
  onCancel,
  onImportPoints,
  onImportTable,
  returnFocusTo,
  vectorOnly = false,
}: SpreadsheetImportDialogProps) {
  const initialDefaults = useMemo(
    () => coordinateDefaults(pending.table, vectorOnly),
    [pending.table, vectorOnly],
  );
  const [table, setTable] = useState(pending.table);
  const [worksheetName, setWorksheetName] = useState(pending.table.provenance.worksheetName ?? '');
  const [intent, setIntent] = useState<ImportIntent>(initialDefaults.intent);
  const [xField, setXField] = useState(initialDefaults.xField);
  const [yField, setYField] = useState(initialDefaults.yField);
  const [crsChoice, setCrsChoice] = useState<CoordinateSystemChoice>(initialDefaults.crsChoice);
  const [crsQuery, setCrsQuery] = useState('');
  const [crsResults, setCrsResults] = useState<readonly EpsgSearchResult[]>([]);
  const [selectedCrs, setSelectedCrs] = useState<SpreadsheetSourceCrs | null>(
    initialDefaults.selectedCrs,
  );
  const [axisSwapMessage, setAxisSwapMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const loadGeneration = useRef(0);
  const titleId = useId();
  const suggestions = useMemo(() => suggestCoordinateFields(table), [table]);

  useEffect(() => () => void ++loadGeneration.current, []);

  useEffect(() => {
    if (crsChoice !== 'projected' || crsQuery.trim().length < 2) return;
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      void import('../crs/epsgCatalogue')
        .then(({ searchEpsgCatalogue }) => {
          if (!cancelled) setCrsResults(searchEpsgCatalogue(crsQuery));
        })
        .catch(() => {
          if (!cancelled) setErrorMessage('Placeio Maps could not load the offline CRS catalogue.');
        });
    }, 150);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [crsChoice, crsQuery]);

  const sourceCrs = useMemo<SpreadsheetSourceCrs | null>(
    () => (crsChoice === 'wgs84' ? { kind: 'wgs84' } : selectedCrs),
    [crsChoice, selectedCrs],
  );
  const preview = useMemo(
    () =>
      intent === 'points' && xField && yField && sourceCrs
        ? previewSpreadsheetPoints(table, xField, yField, sourceCrs)
        : null,
    [intent, sourceCrs, table, xField, yField],
  );
  const likelyAxisReversal = useMemo(
    () => intent === 'points' && hasLikelyWgs84AxisReversal(table, xField, yField),
    [intent, table, xField, yField],
  );

  const changeWorksheet = async (name: string) => {
    if (!pending.workbook || name === worksheetName) return;
    const generation = ++loadGeneration.current;
    setWorksheetName(name);
    setIsLoading(true);
    setErrorMessage(null);
    let result;
    try {
      result = await pending.workbook.importWorksheet(name);
    } catch {
      if (generation !== loadGeneration.current) return;
      setIsLoading(false);
      setErrorMessage('Placeio Maps could not read this worksheet. Try another worksheet.');
      return;
    }
    if (generation !== loadGeneration.current) return;
    setIsLoading(false);
    if (result.ok) {
      const defaults = coordinateDefaults(result.value, vectorOnly);
      setTable(result.value);
      setIntent(defaults.intent);
      setXField(defaults.xField);
      setYField(defaults.yField);
      setCrsChoice(defaults.crsChoice);
      setSelectedCrs(defaults.selectedCrs);
      setCrsQuery('');
      setCrsResults([]);
      setAxisSwapMessage(null);
    } else setErrorMessage(result.error.message);
  };

  const swapCoordinateFields = () => {
    const nextXField = yField;
    const nextYField = xField;
    setXField(nextXField);
    setYField(nextYField);
    setCrsChoice('wgs84');
    setSelectedCrs({ kind: 'wgs84' });
    setCrsQuery('');
    setCrsResults([]);
    setErrorMessage(null);
    setAxisSwapMessage('Coordinate fields swapped. WGS 84 selected.');
  };

  const chooseProjectedCrs = async (result: EpsgSearchResult) => {
    if (result.availability !== 'supported') return;
    const generation = ++loadGeneration.current;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const { resolveEpsgCatalogueCrs } = await import('../crs/epsgCatalogue');
      const resolved = await resolveEpsgCatalogueCrs(result.code);
      if (generation !== loadGeneration.current) return;
      if (resolved.ok) setSelectedCrs({ kind: 'resolved', value: resolved.value });
      else setErrorMessage(resolved.error.message);
    } catch {
      if (generation !== loadGeneration.current) return;
      setErrorMessage('Placeio Maps could not load this coordinate system locally.');
    } finally {
      if (generation === loadGeneration.current) setIsLoading(false);
    }
  };

  const confirm = () => {
    setErrorMessage(null);
    if (intent === 'table') {
      onImportTable(table);
      return;
    }
    if (!sourceCrs) {
      setErrorMessage('Choose the source coordinate system.');
      return;
    }
    const result = createSpreadsheetPointLayer(table, xField, yField, sourceCrs);
    if (!result.ok) {
      setErrorMessage(result.error.message);
      return;
    }
    onImportPoints(result.value.imported, {
      valid: result.value.preview.validPointCount,
      invalid: result.value.preview.invalidRowCount,
    });
  };

  return (
    <DialogShell
      className="spreadsheet-import-dialog"
      closeOnEscape={!isLoading}
      element="section"
      onClose={onCancel}
      returnFocusTo={returnFocusTo}
      titleId={titleId}
    >
      <h2 id={titleId}>{vectorOnly ? 'Create replacement points' : 'Import spreadsheet'}</h2>
      <p>
        {table.rows.length.toLocaleString()} rows · {table.fields.length.toLocaleString()} columns
      </p>

      {pending.workbook && pending.workbook.worksheets.length > 1 ? (
        <label>
          Worksheet
          <select
            aria-label="Worksheet"
            disabled={isLoading}
            onChange={(event) => void changeWorksheet(event.currentTarget.value)}
            value={worksheetName}
          >
            {pending.workbook.worksheets.map((worksheet) => (
              <option key={worksheet.name} value={worksheet.name}>
                {worksheet.name}
                {worksheet.hidden ? ' (Hidden)' : ''}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {vectorOnly ? (
        <p>Create point features from coordinate columns in this spreadsheet.</p>
      ) : (
        <fieldset className="spreadsheet-intent-options">
          <legend>Import as</legend>
          <label>
            <input
              checked={intent === 'table'}
              name="spreadsheet-intent"
              onChange={() => {
                setIntent('table');
                setAxisSwapMessage(null);
              }}
              type="radio"
              value="table"
            />
            <span>
              <strong>Table</strong>
              <small>Use the rows for joins and attribute lookup.</small>
            </span>
          </label>
          <label>
            <input
              checked={intent === 'points'}
              name="spreadsheet-intent"
              onChange={() => {
                setIntent('points');
                setAxisSwapMessage(null);
              }}
              type="radio"
              value="points"
            />
            <span>
              <strong>Create points from coordinates</strong>
              <small>Keep every source column as a point attribute.</small>
            </span>
          </label>
        </fieldset>
      )}

      {intent === 'points' ? (
        <div className="spreadsheet-coordinate-options">
          {suggestions.strong ? (
            <p className="spreadsheet-suggestion">
              Likely longitude and latitude fields were preselected.
            </p>
          ) : null}
          <label>
            X / Longitude field
            <select
              aria-label="X / Longitude field"
              onChange={(event) => {
                setXField(event.currentTarget.value);
                setAxisSwapMessage(null);
              }}
              value={xField}
            >
              {table.fields.map((field) => (
                <option key={field.name} value={field.name}>
                  {fieldOptionLabel(table, field.name)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Y / Latitude field
            <select
              aria-label="Y / Latitude field"
              onChange={(event) => {
                setYField(event.currentTarget.value);
                setAxisSwapMessage(null);
              }}
              value={yField}
            >
              {table.fields.map((field) => (
                <option key={field.name} value={field.name}>
                  {fieldOptionLabel(table, field.name)}
                </option>
              ))}
            </select>
          </label>
          {likelyAxisReversal || axisSwapMessage ? (
            <div aria-live="polite" className="spreadsheet-axis-hint">
              {likelyAxisReversal ? (
                <>
                  <span>
                    <strong>These coordinate fields may be reversed.</strong> X looks like latitude
                    and Y looks like longitude.
                  </span>
                  <button onClick={swapCoordinateFields} type="button">
                    Swap X and Y
                  </button>
                </>
              ) : (
                <span>{axisSwapMessage}</span>
              )}
            </div>
          ) : null}
          <label>
            Coordinate system
            <select
              aria-label="Coordinate system"
              onChange={(event) => {
                const choice = event.currentTarget.value as CoordinateSystemChoice;
                setCrsChoice(choice);
                setSelectedCrs(choice === 'wgs84' ? { kind: 'wgs84' } : null);
                setErrorMessage(null);
                setAxisSwapMessage(null);
              }}
              value={crsChoice}
            >
              <option value="wgs84">WGS 84 (longitude, latitude)</option>
              <option value="projected">Another projected CRS…</option>
            </select>
          </label>

          {crsChoice === 'projected' ? (
            <>
              <label>
                Search source CRS by EPSG code or name
                <input
                  aria-label="Search spreadsheet coordinate systems"
                  autoComplete="off"
                  onChange={(event) => {
                    setCrsQuery(event.currentTarget.value);
                    setCrsResults([]);
                    setSelectedCrs(null);
                  }}
                  placeholder="For example: 28356 or UTM zone 56S"
                  type="search"
                  value={crsQuery}
                />
              </label>
              {crsResults.length > 0 ? (
                <ul
                  aria-label="Spreadsheet coordinate system search results"
                  className="crs-search-results"
                >
                  {crsResults.map((result) => (
                    <li key={result.code}>
                      <button
                        className={
                          result.availability === 'supported' ? '' : 'crs-result-unsupported'
                        }
                        disabled={result.availability !== 'supported'}
                        onClick={() => void chooseProjectedCrs(result)}
                        type="button"
                      >
                        <strong>{result.identifier}</strong> — {result.name}
                        {result.unsupportedReason ? (
                          <small>{result.unsupportedReason}</small>
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              {selectedCrs?.kind === 'resolved' ? (
                <p className="spreadsheet-suggestion" role="status">
                  Selected {selectedCrs.value.metadata.identifier} —{' '}
                  {selectedCrs.value.metadata.name}
                </p>
              ) : null}
            </>
          ) : null}

          {preview ? (
            <dl className="spreadsheet-point-preview">
              <div>
                <dt>Rows</dt>
                <dd>{preview.totalRowCount.toLocaleString()}</dd>
              </div>
              <div>
                <dt>Valid points</dt>
                <dd>{preview.validPointCount.toLocaleString()}</dd>
              </div>
              <div>
                <dt>Invalid coordinates</dt>
                <dd>{preview.invalidRowCount.toLocaleString()}</dd>
              </div>
            </dl>
          ) : (
            <p className="spreadsheet-suggestion">
              Choose a supported source CRS to preview point counts.
            </p>
          )}
        </div>
      ) : null}

      {isLoading ? <p role="status">Reading spreadsheet data locally…</p> : null}
      {errorMessage ? (
        <p className="import-error" role="alert">
          {errorMessage}
        </p>
      ) : null}
      <div className="import-dialog-actions">
        <button onClick={onCancel} type="button">
          Cancel
        </button>
        <button
          className="primary-action"
          disabled={isLoading || (intent === 'points' && (!xField || !yField || !sourceCrs))}
          onClick={confirm}
          type="button"
        >
          {intent === 'points' ? 'Create point layer' : 'Import as table'}
        </button>
      </div>
    </DialogShell>
  );
}
